'use client'

import { useLocale } from 'next-intl'
import { ExternalLink } from '@/components/ui/icons'
import { DocsShell } from '@/components/docs/DocsShell'
import { YouTubePlayer } from '@/components/YouTubePlayer'
import { toDocsLocale } from '@/lib/docs'
import { getCommunityLinks } from '../learn-data'
import { COMMUNITY_PAGE_COPY } from '../learn-copy'
import { usePublishedOverrides } from '@/i18n/useContentOverrides'
import { applyCommunityLinkOverrides, applyCommunityPageCopyOverrides } from '@/lib/i18n/learnRouteMessages'

/** Parse a YouTube watch/short/embed URL into its video id + optional start offset. */
function parseYouTube(href: string): { id: string; start?: number } | null {
  try {
    const url = new URL(href)
    const host = url.hostname.replace(/^www\./, '').replace(/^m\./, '')
    let id: string | null = null
    if (host === 'youtu.be') {
      id = url.pathname.slice(1) || null
    } else if (host === 'youtube.com') {
      if (url.pathname === '/watch') id = url.searchParams.get('v')
      else if (url.pathname.startsWith('/embed/')) id = url.pathname.slice('/embed/'.length)
    }
    if (!id) return null
    const t = url.searchParams.get('t') ?? url.searchParams.get('start')
    return { id, start: t ? parseYouTubeTime(t) : undefined }
  } catch {
    return null
  }
}

/** Accepts plain seconds ("576") or the "1h2m3s" YouTube timestamp form. */
function parseYouTubeTime(raw: string): number | undefined {
  if (/^\d+$/.test(raw)) return Number(raw)
  const m = raw.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/)
  if (!m) return undefined
  const total = (Number(m[1]) || 0) * 3600 + (Number(m[2]) || 0) * 60 + (Number(m[3]) || 0)
  return total || undefined
}

export default function CommunityPage() {
  const locale = toDocsLocale(useLocale())
  const overrides = usePublishedOverrides(locale)
  const copy = applyCommunityPageCopyOverrides(COMMUNITY_PAGE_COPY[locale], overrides)
  const linksLocale = locale
  const links = applyCommunityLinkOverrides(getCommunityLinks(linksLocale), overrides)

  return (
    <DocsShell>
      <header className="mb-10">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">{copy.title}</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{copy.intro}</p>
      </header>

      <div className="grid grid-cols-1 gap-x-4 gap-y-10 sm:grid-cols-2">
        {links.map(link => {
          const yt = parseYouTube(link.href)
          return (
            <div key={link.href}>
              {/* YouTube clips play inline (click to play); other sources link out. */}
              {yt ? (
                <YouTubePlayer
                  videoId={yt.id}
                  startSeconds={yt.start}
                  posterUrl={link.thumbnailUrl}
                  title={link.title}
                  className="border border-white/10 bg-card"
                />
              ) : (
                <a href={link.href} target="_blank" rel="noopener noreferrer" className="group block">
                  <div className="relative aspect-video overflow-hidden rounded-xl border border-white/10 bg-card">
                    {/* eslint-disable-next-line @next/next/no-img-element -- external thumbnail, parity with YouTubePlayer */}
                    <img
                      src={link.thumbnailUrl}
                      alt={link.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                </a>
              )}

              <a href={link.href} target="_blank" rel="noopener noreferrer" className="group mt-2.5 block px-0.5">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-medium text-foreground">{link.title}</h3>
                  <ExternalLink className="h-3 w-3 text-muted-foreground/40" />
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground/60">
                  {link.author}
                  {/* eslint-disable-next-line i18next/no-literal-string -- decorative separator */}
                  {link.meta && <span className="text-muted-foreground/40"> · {link.meta}</span>}
                </p>
                <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-muted-foreground">{link.description}</p>
              </a>
            </div>
          )
        })}
      </div>
    </DocsShell>
  )
}
